import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { Logo } from "./Logo";
import { trackEvent } from "@/utils/analytics";
import { useAuth } from "@/context/AuthContext";

const links = [
  { label: "Engines", href: "#engines" },
  { label: "All engines", href: "/engines" },
  { label: "What's new", href: "/changelog" },
  { label: "Enterprise", href: "#enterprise" },
  { label: "Pricing", href: "#pricing" },
];

export const Nav = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const onHome = pathname === "/";
  const hrefFor = (href) => (onHome || !href.startsWith("#") ? href : `/${href}`);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  return (
    <motion.header
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 bg-page transition-shadow duration-300 ${scrolled || menuOpen ? "border-b border-hairline" : "border-b border-transparent"}`}
      data-testid="nav-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between gap-6">
        <a
          href={onHome ? "#top" : "/"}
          data-testid="nav-logo"
          className="shrink-0 hover:opacity-70 transition-opacity duration-200"
        >
          <Logo size={26} />
        </a>

        <nav className="hidden lg:flex items-center gap-8 absolute left-1/2 -translate-x-1/2">
          {links.map((l) => (
            <a
              key={l.href}
              href={hrefFor(l.href)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="text-[13px] font-medium text-fog hover:text-ink hover:underline underline-offset-4 decoration-1 transition-colors duration-200"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-4 shrink-0">
          {user ? (
            <a
              href="/account"
              data-testid="nav-account-link"
              className="hidden sm:inline-flex items-center min-h-[44px] text-[13px] font-medium text-fog hover:text-ink transition-colors duration-200"
            >
              Account
            </a>
          ) : (
            <a
              href="/login"
              data-testid="nav-signin-link"
              className="hidden sm:inline-flex items-center min-h-[44px] text-[13px] font-medium text-fog hover:text-ink transition-colors duration-200"
            >
              Sign in
            </a>
          )}
          <a
            href={hrefFor("#pricing")}
            data-testid="nav-cta-button"
            onClick={() => trackEvent("nav_cta_click", { source: "header" })}
            className="group inline-flex items-center gap-1.5 min-h-[44px] px-4 v4-btn-primary text-[13px] font-semibold transition-colors duration-200"
          >
            Choose plan
            <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200" />
          </a>
          <button
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="nav-menu-button"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            className="lg:hidden w-11 h-11 border border-hairline rounded-full flex items-center justify-center text-ink transition-colors duration-200 hover:border-ink"
          >
            {menuOpen ? <X className="w-4 h-4" strokeWidth={1.5} /> : <Menu className="w-4 h-4" strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-page border-b border-hairline"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 pb-4 pt-2">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={hrefFor(l.href)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="block py-3 text-[15px] font-medium text-fog hover:text-ink transition-colors duration-200 border-b border-hairline last:border-0"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={user ? "/account" : "/login"}
                data-testid="nav-mobile-auth-link"
                className="block py-3 text-[15px] font-medium text-fog hover:text-ink transition-colors duration-200"
              >
                {user ? "Account" : "Sign in"}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
